// src/features/shopping/ShoppingList.tsx
import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Typography,
  Container,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  IconButton,
  ListItemSecondaryAction,
  Menu,
  Snackbar,
  Grid,
} from "@mui/material";
import { ExpandMore, ShoppingCart, Warning, Add, Delete, Edit, Share, ContentCopy, Print, Email, Restaurant } from "@mui/icons-material";
import { useParams, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import api from "../../services/api";
import { trackEvent, AnalyticsEvents } from "../../services/analytics";

interface ShoppingItem {
  item_id: number;
  ingredient_id: number | null;
  quantity: number;
  unit: string;
  aisle: string;
  is_purchased: boolean;
  ingredient_name?: string;
  custom_item_name?: string;
  display_name?: string;
}

interface ShoppingListData {
  shopping_list_id: number;
  meal_plan_id?: number;
  created_at: string;
  items: ShoppingItem[];
  is_dynamic?: boolean;
}

interface MealForSelector {
  meal_id: number;
  meal_type: string;
  assigned_date: string;
  recipe_name: string;
  image_url: string | null;
  on_list: boolean;
  plan_id: number;
}

const ShoppingList: React.FC = () => {
  const { planId } = useParams<{ planId: string }>();
  const navigate = useNavigate();
  const [shoppingList, setShoppingList] = useState<ShoppingListData | null>(null);
  const [loading, setLoading] = useState(true);
  const [generatingList, setGeneratingList] = useState(false);
  const [approvalError, setApprovalError] = useState<string | null>(null);
  const [useDynamicList, setUseDynamicList] = useState(false);

  // Meal Selector State
  const [meals, setMeals] = useState<MealForSelector[]>([]);
  const [mealSelectorOpen, setMealSelectorOpen] = useState(false);
  const [togglingMealIds, setTogglingMealIds] = useState<Set<number>>(new Set());

  // Add Item Dialog State
  const [addItemDialogOpen, setAddItemDialogOpen] = useState(false);
  const [newItemName, setNewItemName] = useState('');
  const [newItemQuantity, setNewItemQuantity] = useState('1');
  const [newItemUnit, setNewItemUnit] = useState('item');
  const [newItemAisle, setNewItemAisle] = useState('Other');
  const [addingItem, setAddingItem] = useState(false);

  // Edit Item State
  const [editingItemId, setEditingItemId] = useState<number | null>(null);
  const [editQuantity, setEditQuantity] = useState('');

  // Share Menu State
  const [shareMenuAnchor, setShareMenuAnchor] = useState<null | HTMLElement>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  // Grocery store section organization
  const sectionOrder = [
    'Produce',
    'Meat & Seafood',
    'Dairy & Eggs',
    'Bakery',
    'Pantry Staples',
    'Frozen',
    'Condiments & Sauces',
    'Beverages',
    'Other'
  ];

  const sectionIcons = {
    'Produce': '🥬',
    'Meat & Seafood': '🥩',
    'Dairy & Eggs': '🥛',
    'Bakery': '🍞',
    'Pantry Staples': '🥫',
    'Frozen': '🧊',
    'Condiments & Sauces': '🍯',
    'Beverages': '🥤',
    'Other': '📦'
  };

  const loadMeals = useCallback(async () => {
    try {
      const response = await api.get('/shopping-list/me/meals');
      setMeals(response.data.meals || []);
    } catch (err) {
      console.error('Error loading meals for selector:', err);
    }
  }, []);

  const loadShoppingList = useCallback(async () => {
    try {
      setLoading(true);

      // Try per-user dynamic list first
      const meResponse = await api.get('/shopping-list/me');
      const meList = meResponse.data.shoppingList;

      if (meList && (meList.items?.length > 0 || meList.shopping_list_id)) {
        setShoppingList(meList);
        setUseDynamicList(true);
        trackEvent(AnalyticsEvents.SHOPPING_LIST_VIEWED, { plan_id: planId, source: 'dynamic' });
        return;
      }

      // Fall back to legacy plan-based list
      if (planId) {
        const response = await api.get(`/shopping-list/by-plan/${planId}`);
        setShoppingList(response.data.shoppingList);
        setUseDynamicList(false);
        trackEvent(AnalyticsEvents.SHOPPING_LIST_VIEWED, { plan_id: planId, source: 'plan' });
      } else {
        setShoppingList(null);
        setUseDynamicList(true);
      }
    } catch (err: any) {
      if (err.response?.status === 404) {
        setShoppingList(null);
        setUseDynamicList(true);
      } else {
        console.error('Error loading shopping list:', err);
      }
    } finally {
      setLoading(false);
    }
  }, [planId]);

  useEffect(() => {
    loadShoppingList();
    loadMeals();
  }, [loadShoppingList, loadMeals]);

  const handleToggleMeal = async (meal: MealForSelector) => {
    if (togglingMealIds.has(meal.meal_id)) return;

    const wasOnList = meal.on_list;

    // Optimistic update
    setMeals(prev => prev.map(m =>
      m.meal_id === meal.meal_id ? { ...m, on_list: !wasOnList } : m
    ));
    setTogglingMealIds(prev => new Set(prev).add(meal.meal_id));

    try {
      if (wasOnList) {
        await api.delete(`/shopping-list/me/meals/${meal.meal_id}`);
      } else {
        await api.post('/shopping-list/me/meals', { meal_id: meal.meal_id });
      }

      // Reload shopping list items & meals to get accurate state
      await Promise.all([loadShoppingList(), loadMeals()]);
    } catch (err: any) {
      // Rollback optimistic update
      setMeals(prev => prev.map(m =>
        m.meal_id === meal.meal_id ? { ...m, on_list: wasOnList } : m
      ));
      const message = err.response?.data?.message || 'Failed to update meal';
      setSnackbarMessage(message);
      setSnackbarOpen(true);
    } finally {
      setTogglingMealIds(prev => {
        const next = new Set(prev);
        next.delete(meal.meal_id);
        return next;
      });
    }
  };

  const generateShoppingList = async () => {
    if (!planId) return;

    try {
      setGeneratingList(true);
      setApprovalError(null);
      await api.post('/shopping-list', {
        plan_id: parseInt(planId)
      });

      // Reload the shopping list after generation
      await loadShoppingList();
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to generate shopping list';

      // Check if this is an approval error
      if (errorMessage.toLowerCase().includes('approve') || errorMessage.toLowerCase().includes('review')) {
        setApprovalError(errorMessage);
      } else {
        alert(errorMessage);
      }
    } finally {
      setGeneratingList(false);
    }
  };

  const toggleItemPurchased = async (itemId: number, currentStatus: boolean) => {
    try {
      if (currentStatus) {
        // Mark as unpurchased (update endpoint)
        await api.put(`/shopping-list/${itemId}`, { is_purchased: false });
      } else {
        // Mark as purchased
        await api.put(`/shopping-list/${itemId}/purchase`);
      }

      // Update local state
      setShoppingList(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          items: prev.items.map(item =>
            item.item_id === itemId
              ? { ...item, is_purchased: !currentStatus }
              : item
          )
        };
      });
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to update item');
    }
  };

  const handleAddItem = async () => {
    if (!newItemName.trim()) {
      alert('Please enter an item name');
      return;
    }

    try {
      setAddingItem(true);

      if (useDynamicList || !shoppingList?.shopping_list_id) {
        await api.post('/shopping-list/me/items', {
          custom_item_name: newItemName,
          quantity: parseFloat(newItemQuantity) || 1,
          unit: newItemUnit,
          aisle: newItemAisle
        });
      } else {
        await api.post(`/shopping-list/${shoppingList.shopping_list_id}/items`, {
          custom_item_name: newItemName,
          quantity: parseFloat(newItemQuantity) || 1,
          unit: newItemUnit,
          aisle: newItemAisle
        });
      }

      // Reload list to get accurate state (especially if list was just created)
      await loadShoppingList();

      // Reset form and close dialog
      setNewItemName('');
      setNewItemQuantity('1');
      setNewItemUnit('item');
      setNewItemAisle('Other');
      setAddItemDialogOpen(false);
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to add item');
    } finally {
      setAddingItem(false);
    }
  };

  const handleDeleteItem = async (itemId: number) => {
    if (!window.confirm('Remove this item from your shopping list?')) {
      return;
    }

    try {
      await api.delete(`/shopping-list/${itemId}`);

      // Remove from local state
      setShoppingList(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          items: prev.items.filter(item => item.item_id !== itemId)
        };
      });
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to delete item');
    }
  };

  const handleEditQuantity = async (itemId: number, newQuantity: string) => {
    const quantity = parseFloat(newQuantity);
    if (isNaN(quantity) || quantity <= 0) {
      alert('Please enter a valid quantity');
      return;
    }

    try {
      await api.put(`/shopping-list/${itemId}`, { quantity });

      // Update local state
      setShoppingList(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          items: prev.items.map(item =>
            item.item_id === itemId
              ? { ...item, quantity }
              : item
          )
        };
      });

      setEditingItemId(null);
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to update quantity');
    }
  };

  const organizeItemsBySection = (items: ShoppingItem[]) => {
    const sections: { [key: string]: ShoppingItem[] } = {};

    // Initialize all sections
    sectionOrder.forEach(section => {
      sections[section] = [];
    });

    // Categorize items
    items.forEach(item => {
      const section = mapAisleToSection(item.aisle);
      sections[section].push(item);
    });

    // Remove empty sections for cleaner display
    return Object.entries(sections).filter(([_, items]) => items.length > 0);
  };

  const mapAisleToSection = (aisle: string): string => {
    const normalized = aisle.toLowerCase().trim();

    // Direct matches for standard category names (case-insensitive)
    const directMatches: { [key: string]: string } = {
      'produce': 'Produce',
      'meat & seafood': 'Meat & Seafood',
      'dairy & eggs': 'Dairy & Eggs',
      'bakery': 'Bakery',
      'pantry staples': 'Pantry Staples',
      'frozen': 'Frozen',
      'condiments & sauces': 'Condiments & Sauces',
      'beverages': 'Beverages',
      'other': 'Other',
    };

    if (directMatches[normalized]) {
      return directMatches[normalized];
    }

    // Legacy/partial value mappings
    const legacyMap: { [key: string]: string } = {
      'vegetables': 'Produce',
      'fruits': 'Produce',
      'meat': 'Meat & Seafood',
      'seafood': 'Meat & Seafood',
      'poultry': 'Meat & Seafood',
      'dairy': 'Dairy & Eggs',
      'eggs': 'Dairy & Eggs',
      'cheese': 'Dairy & Eggs',
      'bread': 'Bakery',
      'grains': 'Pantry Staples',
      'canned': 'Pantry Staples',
      'pantry': 'Pantry Staples',
      'spices': 'Pantry Staples',
      'oils': 'Pantry Staples',
      'condiments': 'Condiments & Sauces',
      'sauces': 'Condiments & Sauces',
      'drinks': 'Beverages',
    };

    return legacyMap[normalized] || 'Other';
  };

  const getCompletionStats = () => {
    if (!shoppingList) return { completed: 0, total: 0, percentage: 0 };

    const total = shoppingList.items.length;
    const completed = shoppingList.items.filter(item => item.is_purchased).length;
    const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;

    return { completed, total, percentage };
  };

  // Group meals by date for the selector
  const groupMealsByDate = (mealsList: MealForSelector[]) => {
    const groups: { [date: string]: MealForSelector[] } = {};
    mealsList.forEach(meal => {
      const date = meal.assigned_date;
      if (!groups[date]) groups[date] = [];
      groups[date].push(meal);
    });
    return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
  };

  // Format shopping list as text
  const formatShoppingListText = (): string => {
    if (!shoppingList) return '';

    const stats = getCompletionStats();
    const sections = organizeItemsBySection(shoppingList.items);

    let text = "Shopping List\n";
    text += `${stats.completed} of ${stats.total} items\n`;
    text += "--------------------\n\n";

    sections.forEach(([sectionName, items]) => {
      text += `${sectionName}\n`;
      items.forEach(item => {
        const itemName = item.display_name || item.ingredient_name || item.custom_item_name || 'Item';
        const checkbox = item.is_purchased ? '[x]' : '[ ]';
        text += `  ${checkbox} ${item.quantity} ${item.unit} ${itemName}\n`;
      });
      text += '\n';
    });

    text += "--------------------\n";
    text += "From Snack'd - snackdapp.com";

    return text;
  };

  const handleCopyToClipboard = async () => {
    const text = formatShoppingListText();
    try {
      await navigator.clipboard.writeText(text);
      setSnackbarMessage('Shopping list copied to clipboard!');
      setSnackbarOpen(true);
    } catch (err) {
      setSnackbarMessage('Failed to copy to clipboard');
      setSnackbarOpen(true);
    }
    setShareMenuAnchor(null);
  };

  const handlePrint = () => {
    setShareMenuAnchor(null);

    const printContent = formatShoppingListText();

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>Shopping List - Snack'd</title>
            <style>
              body { font-family: Arial, sans-serif; padding: 20px; max-width: 600px; margin: 0 auto; }
              h1 { color: #175873; }
              pre { white-space: pre-wrap; font-family: inherit; line-height: 1.6; }
            </style>
          </head>
          <body>
            <h1>Shopping List</h1>
            <pre>${printContent}</pre>
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
    }
  };

  const handleEmailList = () => {
    const text = formatShoppingListText();
    const subject = encodeURIComponent('My Shopping List from Snack\'d');
    const body = encodeURIComponent(text);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
    setShareMenuAnchor(null);
  };

  // Meal Selector Panel
  const mealsOnList = meals.filter(m => m.on_list).length;

  const renderMealSelector = () => {
    if (meals.length === 0) return null;

    const groupedMeals = groupMealsByDate(meals);

    return (
      <Accordion
        expanded={mealSelectorOpen}
        onChange={() => setMealSelectorOpen(prev => !prev)}
        sx={{ mb: 3 }}
      >
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <Restaurant color="secondary" />
            <Typography variant="h6">Meals</Typography>
            <Chip
              label={`${mealsOnList} of ${meals.length} on list`}
              size="small"
              color={mealsOnList > 0 ? "secondary" : "default"}
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {groupedMeals.map(([date, dateMeals]) => (
            <Box key={date} sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontWeight: 600 }}>
                {dayjs(date).format('ddd, MMM D')}
              </Typography>
              <Grid container spacing={1.5}>
                {dateMeals.map(meal => {
                  const isToggling = togglingMealIds.has(meal.meal_id);
                  return (
                    <Grid key={meal.meal_id} size={{ xs: 6, sm: 6, md: 4 }}>
                      <Box
                        onClick={() => handleToggleMeal(meal)}
                        sx={{
                          position: 'relative',
                          borderRadius: 2,
                          overflow: 'hidden',
                          cursor: isToggling ? 'wait' : 'pointer',
                          border: 2,
                          borderColor: meal.on_list ? 'secondary.main' : 'grey.300',
                          opacity: isToggling ? 0.6 : 1,
                          transition: 'border-color 0.2s, opacity 0.2s',
                          '&:hover': {
                            borderColor: meal.on_list ? 'secondary.dark' : 'grey.500',
                          },
                        }}
                      >
                        {/* Image / Placeholder */}
                        <Box
                          sx={{
                            width: '100%',
                            height: 100,
                            backgroundColor: meal.image_url ? undefined : 'grey.200',
                            backgroundImage: meal.image_url ? `url(${meal.image_url})` : undefined,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                          }}
                        />

                        {/* Checkbox overlay */}
                        <Checkbox
                          checked={meal.on_list}
                          size="small"
                          color="secondary"
                          sx={{
                            position: 'absolute',
                            top: 2,
                            left: 2,
                            backgroundColor: 'rgba(255,255,255,0.85)',
                            borderRadius: 1,
                            p: 0.25,
                            '&:hover': { backgroundColor: 'rgba(255,255,255,0.95)' },
                          }}
                          onClick={e => e.stopPropagation()}
                          onChange={() => handleToggleMeal(meal)}
                          disabled={isToggling}
                        />

                        {/* Meal type chip */}
                        <Chip
                          label={meal.meal_type}
                          size="small"
                          sx={{
                            position: 'absolute',
                            top: 6,
                            right: 6,
                            fontSize: '0.65rem',
                            height: 20,
                            textTransform: 'capitalize',
                            backgroundColor: 'rgba(255,255,255,0.85)',
                          }}
                        />

                        {/* Recipe name */}
                        <Box sx={{ px: 1, py: 0.75 }}>
                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: 600,
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              lineHeight: 1.3,
                            }}
                          >
                            {meal.recipe_name}
                          </Typography>
                        </Box>

                        {/* Loading overlay */}
                        {isToggling && (
                          <Box
                            sx={{
                              position: 'absolute',
                              inset: 0,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: 'rgba(255,255,255,0.5)',
                            }}
                          >
                            <CircularProgress size={20} />
                          </Box>
                        )}
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          ))}
        </AccordionDetails>
      </Accordion>
    );
  };

  if (loading) {
    return (
      <Container sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Loading shopping list...
        </Typography>
      </Container>
    );
  }

  // Empty state — show meal selector + optional generate button
  if (!shoppingList || shoppingList.items.length === 0) {
    return (
      <Container sx={{ mt: 4 }}>
        <Typography variant="h4" gutterBottom>
          Shopping List
        </Typography>

        {/* Meal Selector always shown when meals available */}
        {renderMealSelector()}

        {approvalError ? (
          <Alert severity="warning" sx={{ mb: 3 }} icon={<Warning />}>
            <Typography variant="subtitle1" gutterBottom>
              <strong>Meal Plan Not Approved Yet</strong>
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              {approvalError}
            </Typography>
            <Button
              variant="contained"
              color="warning"
              size="small"
              onClick={() => navigate(`/meal-plans/${planId}/review`)}
            >
              Review & Approve Meal Plan
            </Button>
          </Alert>
        ) : meals.length > 0 ? (
          <Alert severity="info" sx={{ mb: 3 }}>
            Select meals above to build your shopping list, or add custom items.
          </Alert>
        ) : (
          <Alert severity="info" sx={{ mb: 3 }}>
            No shopping list has been generated for this meal plan yet.
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setAddItemDialogOpen(true)}
          >
            Add Custom Item
          </Button>
          {planId && (
            <Button
              variant="outlined"
              onClick={generateShoppingList}
              disabled={generatingList}
              startIcon={generatingList ? <CircularProgress size={20} /> : <ShoppingCart />}
            >
              {generatingList ? 'Generating...' : 'Generate Full List'}
            </Button>
          )}
        </Box>

        {/* Add Item Dialog (also needed in empty state) */}
        {renderAddItemDialog()}

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={3000}
          onClose={() => setSnackbarOpen(false)}
          message={snackbarMessage}
        />
      </Container>
    );
  }

  const stats = getCompletionStats();
  const organizedSections = organizeItemsBySection(shoppingList.items);

  return (
    <Container sx={{ mt: 4, mb: 6 }}>
      <Typography variant="h4" gutterBottom>
        Shopping List
      </Typography>

      {/* Meal Selector */}
      {renderMealSelector()}

      {/* Progress Summary */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h6">
                Shopping Progress
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {stats.completed} of {stats.total} items completed
              </Typography>
            </Box>
            <Box textAlign="center">
              <Chip
                label={`${stats.percentage}%`}
                color={stats.percentage === 100 ? "success" : "primary"}
                size="medium"
              />
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setAddItemDialogOpen(true)}
          sx={{ flex: 1 }}
        >
          Add Custom Item
        </Button>
        <Button
          variant="outlined"
          startIcon={<Share />}
          onClick={(e) => setShareMenuAnchor(e.currentTarget)}
        >
          Share
        </Button>
        <Menu
          anchorEl={shareMenuAnchor}
          open={Boolean(shareMenuAnchor)}
          onClose={() => setShareMenuAnchor(null)}
        >
          <MenuItem onClick={handleCopyToClipboard}>
            <ListItemIcon><ContentCopy fontSize="small" /></ListItemIcon>
            <ListItemText>Copy to Clipboard</ListItemText>
          </MenuItem>
          <MenuItem onClick={handlePrint}>
            <ListItemIcon><Print fontSize="small" /></ListItemIcon>
            <ListItemText>Print / Save as PDF</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleEmailList}>
            <ListItemIcon><Email fontSize="small" /></ListItemIcon>
            <ListItemText>Email List</ListItemText>
          </MenuItem>
        </Menu>
      </Box>

      {/* Shopping Sections */}
      {organizedSections.map(([sectionName, items]) => (
        <Accordion key={sectionName} defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box display="flex" alignItems="center" gap={2}>
              <Typography variant="h6">
                {sectionIcons[sectionName as keyof typeof sectionIcons]} {sectionName}
              </Typography>
              <Chip
                label={`${items.filter(item => item.is_purchased).length}/${items.length}`}
                size="small"
                color={items.every(item => item.is_purchased) ? "success" : "default"}
              />
            </Box>
          </AccordionSummary>

          <AccordionDetails>
            <List>
              {items.map((item, index) => (
                <React.Fragment key={item.item_id}>
                  <ListItem
                    sx={{
                      opacity: item.is_purchased ? 0.6 : 1,
                      textDecoration: item.is_purchased ? 'line-through' : 'none'
                    }}
                  >
                    <ListItemIcon>
                      <Checkbox
                        checked={item.is_purchased}
                        color="primary"
                        onChange={() => toggleItemPurchased(item.item_id, item.is_purchased)}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1}>
                          {editingItemId === item.item_id ? (
                            <TextField
                              size="small"
                              type="number"
                              value={editQuantity}
                              onChange={(e) => setEditQuantity(e.target.value)}
                              onBlur={() => handleEditQuantity(item.item_id, editQuantity)}
                              onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                  handleEditQuantity(item.item_id, editQuantity);
                                }
                              }}
                              autoFocus
                              sx={{ width: '80px' }}
                            />
                          ) : (
                            <Typography variant="body1" component="span">
                              <strong>{item.quantity} {item.unit}</strong>
                            </Typography>
                          )}
                          <Typography variant="body1">
                            {item.display_name || item.ingredient_name || item.custom_item_name || `Item ${item.item_id}`}
                          </Typography>
                          {item.custom_item_name && (
                            <Chip label="Custom" size="small" color="secondary" />
                          )}
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={() => {
                          setEditingItemId(item.item_id);
                          setEditQuantity(item.quantity.toString());
                        }}
                        sx={{ mr: 1 }}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={() => handleDeleteItem(item.item_id)}
                        color="error"
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                  {index < items.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </AccordionDetails>
        </Accordion>
      ))}

      {/* Completion Message */}
      {stats.percentage === 100 && (
        <Alert severity="success" sx={{ mt: 3 }}>
          Shopping complete! You have everything you need for your meal plan.
        </Alert>
      )}

      {/* Add Item Dialog */}
      {renderAddItemDialog()}

      {/* Snackbar for feedback */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </Container>
  );

  function renderAddItemDialog() {
    return (
      <Dialog open={addItemDialogOpen} onClose={() => setAddItemDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Custom Item</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Item Name"
              value={newItemName}
              onChange={(e) => setNewItemName(e.target.value)}
              fullWidth
              required
              autoFocus
              placeholder="e.g., Paper towels, Aluminum foil"
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Quantity"
                type="number"
                value={newItemQuantity}
                onChange={(e) => setNewItemQuantity(e.target.value)}
                inputProps={{ min: 0.1, step: 0.1 }}
                sx={{ flex: 1 }}
              />
              <TextField
                label="Unit"
                value={newItemUnit}
                onChange={(e) => setNewItemUnit(e.target.value)}
                sx={{ flex: 1 }}
                placeholder="e.g., item, pack"
              />
            </Box>
            <TextField
              label="Category"
              select
              value={newItemAisle}
              onChange={(e) => setNewItemAisle(e.target.value)}
              fullWidth
            >
              {sectionOrder.map((section) => (
                <MenuItem key={section} value={section}>
                  {sectionIcons[section as keyof typeof sectionIcons]} {section}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddItemDialogOpen(false)} disabled={addingItem}>
            Cancel
          </Button>
          <Button
            onClick={handleAddItem}
            variant="contained"
            disabled={addingItem || !newItemName.trim()}
          >
            {addingItem ? <CircularProgress size={24} /> : 'Add Item'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
};

export default ShoppingList;
